/**
 * Wine Club Settings API – Allure backend
 * GET: https://apps.allurecommerce.com:9096/wineclubsettingget
 * Insert/Edit/Delete: https://apps.allurecommerce.com:9096/wineClubSetting
 */

const WINE_CLUB_SETTING_GET = "https://apps.allurecommerce.com:9096/wineclubsettingget";
const WINE_CLUB_SETTING_BASE = "https://apps.allurecommerce.com:9096/wineClubSetting";

/**
 * GET – Full wine club data: { settings: [], plan: {} }.
 * Use settings for discount tiers; use plan.products for product-wise sellingPlanId in cart.
 * @returns {Promise<{ settings: Array, plan: Object|null }>}
 */
export async function getWineClubData() {
  const res = await fetch(WINE_CLUB_SETTING_GET, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Wine club fetch failed: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  if (Array.isArray(data)) {
    return { settings: data, plan: null };
  }
  return {
    settings: data?.settings ?? data?.data ?? [],
    plan: data?.plan ?? null,
  };
}

/**
 * GET – List of wine club settings only (backward compat).
 * @returns {Promise<Array>}
 */
export async function getWineClubSettings() {
  const { settings } = await getWineClubData();
  return Array.isArray(settings) ? settings : [];
}

/**
 * POST – Create a new wine club setting
 * @param {Object} payload – Setting JSON to create
 * @returns {Promise<Object>} Created setting (as returned by backend)
 */
export async function createWineClubSetting(payload) {
  const res = await fetch(WINE_CLUB_SETTING_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Wine club setting create failed: ${res.status} – ${text}`);
  }
  return res.json();
}

/**
 * PUT – Update an existing wine club setting
 * @param {string} id – Setting id
 * @param {Object} payload – Setting JSON to update
 * @returns {Promise<Object>} Updated setting (as returned by backend)
 */
export async function updateWineClubSetting(id, payload) {
  const url = `${WINE_CLUB_SETTING_BASE}/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Wine club setting update failed: ${res.status} – ${text}`);
  }
  return res.json();
}

/**
 * DELETE – Delete a wine club setting
 * @param {string} id – Setting id
 * @returns {Promise<void>}
 */
export async function deleteWineClubSetting(id) {
  const url = `${WINE_CLUB_SETTING_BASE}/${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Wine club setting delete failed: ${res.status} – ${text}`);
  }
  if (res.headers.get("content-type")?.includes("application/json")) {
    return res.json();
  }
}

export default {
  getWineClubSettings,
  createWineClubSetting,
  updateWineClubSetting,
  deleteWineClubSetting,
};
